/**
 * News is the one piece of content on this page that wants a real, shareable
 * URL — a patch note posted on Discord has to land on the article, not on
 * the landing page with the token silently dropped, which is exactly the
 * trap `deep-link.ts` documents for the two email links.
 *
 * **Still not a router.** `deep-link.ts` already makes the case: a history
 * stack around a WebGL canvas is not warranted by a couple of public,
 * content-only pages. This is the same shape one step further — a pure path
 * parser, a `navigate()` that pushes state instead of replacing it (a
 * visitor reading the news list, then an article, then pressing back,
 * expects the list back), and a `popstate` listener so the browser's own
 * back/forward buttons work without any of this depending on Vue's
 * reactivity to notice a URL change it did not cause.
 *
 * Both the Vite dev server and the production static server
 * (`server.js`, `caddy/Caddyfile`'s catch-all) already fall back to
 * `index.html` for any unknown path, which is what makes a hard refresh on
 * `/news/some-slug` work at all — nothing here had to change on that side.
 */

export type NewsRoute = { kind: 'list' } | { kind: 'article'; slug: string };

/**
 * A path with no usable slug after `/news/` is not an article route — it
 * falls back to the list rather than a page that can only 404, the same
 * "absent is not broken" call `parseDeepLink` makes for a bare token-less
 * link.
 */
export function parseNewsRoute(pathname: string): NewsRoute | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/news') return { kind: 'list' };

  const match = /^\/news\/([^/]+)$/.exec(path);
  if (!match) return null;

  const slug = decodeURIComponent(match[1]).trim();
  return slug ? { kind: 'article', slug } : { kind: 'list' };
}

/** Reads the live URL. The one impure line, kept out of the parser. */
export function readNewsRoute(): NewsRoute | null {
  if (typeof window === 'undefined') return null;
  return parseNewsRoute(window.location.pathname);
}

/**
 * Moves to a path within this hand-rolled route, or back out of it.
 *
 * `pushState`, deliberately unlike `clearDeepLink`'s `replaceState` — a
 * one-shot token must not survive a back button, but a news list and an
 * article are ordinary pages a visitor expects to navigate between with the
 * one button every browser already gives them for it.
 */
export function navigateNews(path: string): void {
  if (typeof window === 'undefined' || !window.history?.pushState) return;
  window.history.pushState(null, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Leaves the news route entirely — the door back to whatever the app was doing. */
export function leaveNewsRoute(): void {
  if (typeof window === 'undefined' || !window.history?.pushState) return;
  window.history.pushState(null, '', '/');
  window.dispatchEvent(new PopStateEvent('popstate'));
}
